import { styled } from 'styled-components'

import { Breakpoints, Colors, Typography } from '@b-stock/fe-core/design-system'
import type {
  CreditInfo,
  BalanceQualificationResult,
  PayMethod,
  PayMethodDetails,
  BalanceBuyerResult,
  DecoratedBStockCreditsBalanceDetails,
} from '@b-stock/payments-methods-api-client'
import { PayMethodTypeEnum } from '@b-stock/payments-methods-api-client'

export const PageDivider = styled.div`
  width: 0.0625rem;
  height: 100%;
  background-color: ${Colors.Semantic.Neutral.medium_grey};
  align-self: center;

  @media ${Breakpoints.max.small} {
    display: none;
  }
`

// Payment method identifiers
export const PAY_WITH_TERMS = 'payWithTerms'
export const WIRE_TRANSFER = 'wireTransfer'

// Allowed payment method types for secondary payments in split payment scenarios
export const allowedSecondaryPaymentTypes: readonly PayMethodTypeEnum[] = [
  PayMethodTypeEnum.Card,
  PayMethodTypeEnum.Bank,
  PayMethodTypeEnum.StripeFinConn,
  PayMethodTypeEnum.GetbalanceCc,
] as const

// FE-friendly type definitions for payment method details.
export interface CardPaymentDetails {
  card: {
    last4: string
    expMonth: number
    expYear: number
    brand: string
  }
  billingDetails?: {
    name?: string
  }
  owner?: {
    name?: string
  }
}

export interface BankPaymentDetails {
  last4: string
  us_bank_account?: {
    bank_name: string
    account_type: string
    last4: string
  }
}

type BalancePaymentDetails = BalanceBuyerResult
type BStockCreditsPaymentDetails = DecoratedBStockCreditsBalanceDetails

type TypedPaymentMethod =
  | (Omit<PayMethod, 'type' | 'details'> & {
      type: typeof PayMethodTypeEnum.Card
      details: CardPaymentDetails
    })
  | (Omit<PayMethod, 'type' | 'details'> & {
      type:
        | typeof PayMethodTypeEnum.Bank
        | typeof PayMethodTypeEnum.StripeFinConn
      details: BankPaymentDetails
    })
  | (Omit<PayMethod, 'type' | 'details'> & {
      type: typeof PayMethodTypeEnum.Getbalance
      details: BalancePaymentDetails
    })
  | (Omit<PayMethod, 'type' | 'details'> & {
      type: typeof PayMethodTypeEnum.CreditsBalance
      details: BStockCreditsPaymentDetails
    })
  | (Omit<PayMethod, 'type' | 'details'> & {
      type: typeof PayMethodTypeEnum.GetbalanceCc
      details: BalanceBuyerResult
    })
  | (Omit<PayMethod, 'type' | 'details'> & {
      type:
        | typeof PayMethodTypeEnum.Wire
        | typeof PayMethodTypeEnum.AchCreditTransfer
        | typeof PayMethodTypeEnum.CreditTerms
        | (string & {})
      details: Record<string, unknown>
    })

export function asTypedPaymentMethod(method: PayMethod): TypedPaymentMethod {
  return method as TypedPaymentMethod
}

// Override the `details` property
export type NetTermsPayMethod = Omit<PayMethod, 'details'> & {
  details: PayMethodDetails & {
    creditInfo?: CreditInfo
  }
  getbalance: {
    qualification?: BalanceQualificationResult
  }
}

/**
 * Metadata for secondary payment method handling in split payment scenarios
 */
export interface SecondaryPaymentMetadata {
  /**
   * The amount due to be paid by the secondary payment method (in cents)
   */
  amount: number
  /**
   * Whether the split payment feature is enabled
   */
  isEnabled: boolean
  /**
   * The convenience fee that was calculated (in cents)
   */
  convenienceFee: number
  /**
   * The available net terms credit (in cents)
   */
  availableNetTermsCredit: number
}

export const SectionTitle = styled.h2`
  ${Typography.Title3}
  margin: 0.25rem 0;
`

export const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
`
